import type { ApiCallOptions, ApiClient } from "@/clients/api.client";
import type { RevenueCatClient } from "@/clients/revenuecat.client";
import type { AppState, WorkHealth } from "@/store";
import type { Telemetry } from "@/telemetry";
import type {
  ActionError,
  ActionResult,
  AgentConnectionSummary,
  ClientError,
  ClientResult,
  DictationDelivery,
  DictationClientPlatform,
  DictationTrigger,
  LiveRecordingStatus,
  ParagraphSentenceLimit,
  PurchaseCustomerInfo,
  PurchasePackage,
  RemoteConfig,
  SearchResultItem,
  SubscriptionManagementProvider,
  SubscriptionOffer,
  Tag,
  Transcript,
  User,
} from "@/types";

export type ActionStore = {
  getState: () => AppState;
  setState: (
    update: Partial<AppState> | ((state: AppState) => Partial<AppState>),
  ) => void | Promise<void>;
  setSubscribed: (isSubscribed: boolean) => void;
};

export type ActionTelemetry = Pick<Telemetry, "track">;

type TimerHandle = ReturnType<typeof setTimeout>;

export type ActionClock = {
  now: () => number;
  setTimeout: (callback: () => void, timeoutMs: number) => TimerHandle;
  clearTimeout: (handle: TimerHandle) => void;
};

export type CreateActionsDependencies = {
  api: ApiClient;
  // Supplied only by iOS; other platforms keep their existing AI flow.
  requestAiConsent?: (signal?: AbortSignal) => Promise<boolean>;
  requestReview?: () => Promise<void>;
  revenueCat: RevenueCatClient;
  store: ActionStore;
  telemetry: ActionTelemetry;
  clock: ActionClock;
  subscriptionEnvironment?: {
    baseAppUrl: () => string;
    attribution: () => Record<string, string> | undefined;
    managementUrl: (
      provider: SubscriptionManagementProvider,
    ) => string | undefined;
    nativeConfiguration: () => Promise<{
      appUserId?: string;
      email?: string;
      attributes?: Record<string, string>;
      apiKey: string;
      firebaseAppInstanceId?: string;
      enableAdServicesAttribution?: boolean;
    }>;
  };
};

export type TranscriptPollHandle = {
  key: string;
  cancel: () => void;
  done: Promise<ActionResult<Transcript>>;
};

export type TranscriptPollOptions = {
  keySuffix?: string;
  timeoutMs?: number;
  isComplete?: (transcript: Transcript) => boolean;
};

export type AiContentGenerationInput =
  | {
      feature: "summary" | "flashcard" | "quiz";
      transcriptId: string;
      instructions?: string;
    }
  | { feature: "translation"; transcriptId: string; language: string };

const _pendingTranscriptStatuses = new Set<Transcript["status"]>([
  "transcription_started",
  "upload_started",
  "upload_completed",
]);

const _promoteRecentDictationLanguage = (
  recent: readonly string[] | undefined,
  language: string,
): string[] =>
  [language, ...(recent || []).filter((item) => item !== language)].slice(0, 5);

// Same rule as transcript-progress isFreeDraft (actions import no runtime
// modules): a completed free-model draft Premium may redo once.
const _isFreeDraft = (transcript: Transcript): boolean =>
  transcript.status === "transcription_completed" &&
  transcript.transcribedWithPremium === false;

const _success = <Value>(value: Value): ActionResult<Value> => ({
  ok: true,
  value,
});

const _failure = <Value>(error: ActionError): ActionResult<Value> => ({
  ok: false,
  error,
});

const _checkoutEntitlementTimeoutMs = 45_000;
const _checkoutEntitlementPollIntervalMs = 2_000;

const _wait = (clock: ActionClock, timeoutMs: number): Promise<void> =>
  new Promise((resolve) => {
    clock.setTimeout(resolve, timeoutMs);
  });

export const createActions = (dependencies: CreateActionsDependencies) => {
  const { api, clock, revenueCat, store, subscriptionEnvironment, telemetry } =
    dependencies;
  const activePolls = new Map<string, TranscriptPollHandle>();
  // Dictation routing reads the saved profile on the server. If the language
  // changes while recording, keep the audio request behind that one save.
  let pendingDictationLanguageSave: Promise<ActionResult<User>> | undefined;
  const pendingRecordingLanguageSaves = new Map<
    string,
    Promise<ActionResult<User>>
  >();

  const transcript = {
    create: async (
      input: Pick<Transcript, "fileName" | "language" | "type"> & {
        id?: string;
        originalFileUrl?: string;
      },
      options?: ApiCallOptions,
    ): Promise<ActionResult<Transcript>> => {
      const result = await api.transcript.create(
        {
          type: "create",
          payload: {
            id: input.id || `transcript_${10 ** 14 - clock.now()}`,
            type: input.type,
            language: input.language,
            fileName: input.fileName,
            originalFileUrl: input.originalFileUrl,
            status: "transcription_started",
            isFullTranscript: false,
            segments: [],
          },
        },
        options,
      );
      if ("error" in result) return _failure(result.error);
      const created = result.value?.transcript;
      if (!created) return _failure("invalidResponse");

      store.setState((state) => ({
        transcripts: [
          created,
          ...state.transcripts.filter((item) => item.id !== created.id),
        ],
      }));
      telemetry.track("transcript.created", {
        transcriptId: created.id,
        type: created.type,
      });
      return _success(created);
    },

    get: async (
      transcriptId: string,
      options?: ApiCallOptions,
    ): Promise<ActionResult<Transcript>> => {
      const result = await api.transcript.get(transcriptId, options);
      if ("error" in result) return _failure(result.error);
      const received = result.value?.transcript;
      if (!received) return _failure("invalidResponse");

      _reconcileTranscript(store, received, { skipUnchangedRevision: true });
      telemetry.track("transcript.loaded", { transcriptId });
      return _success(received);
    },

    getGuest: async (
      transcriptId: string,
      guestToken: string,
      options?: ApiCallOptions,
    ): Promise<ActionResult<Transcript>> => {
      if (!transcriptId || !guestToken) return _failure("InvalidInput");
      const result = await api.transcript.getWithGuestToken(
        transcriptId,
        guestToken,
        options,
      );
      if ("error" in result) return _failure(result.error);
      const received = result.value?.transcript;
      return received ? _success(received) : _failure("invalidResponse");
    },

    list: async (
      nextCursor?: string,
      options?: ApiCallOptions,
    ): Promise<ActionResult<Transcript[]>> => {
      const result = await api.transcript.list(nextCursor, options);
      if ("error" in result) return _failure(result.error);
      const received = result.value?.transcripts;
      if (!received) return _failure("invalidResponse");

      store.setState((state) => ({
        transcripts: nextCursor
          ? [...state.transcripts, ...received]
          : received,
        transcriptsNextCursor: result.value?.nextCursor,
      }));
      return _success(received);
    },

    set: async (
      value: Transcript,
      options?: ApiCallOptions,
    ): Promise<ActionResult<Transcript>> => {
      const result = await api.transcript.set(value, options);
      if ("error" in result) return _failure(result.error);
      _reconcileTranscript(store, result.value?.transcript || value);
      return _success(result.value?.transcript || value);
    },

    update: async (
      transcriptId: string,
      updates: Partial<Transcript>,
      options?: ApiCallOptions,
    ): Promise<ActionResult<Transcript>> => {
      const previous = store
        .getState()
        .transcripts.find((item) => item.id === transcriptId);
      if (!previous) return _failure("TranscriptNotFound");

      const optimistic = { ...previous, ...updates };
      _replaceTranscript(store, transcriptId, optimistic);
      const result = await api.transcript.update(
        transcriptId,
        updates,
        options,
      );
      if ("error" in result) {
        store.setState((state) => ({
          transcripts: state.transcripts.map((item) =>
            item.id === transcriptId && item === optimistic ? previous : item,
          ),
        }));
        return _failure(result.error);
      }
      const saved = result.value?.transcript;
      if (!saved) {
        store.setState((state) => ({
          transcripts: state.transcripts.map((item) =>
            item.id === transcriptId && item === optimistic ? previous : item,
          ),
        }));
        return _failure("invalidResponse");
      }
      _replaceTranscript(store, transcriptId, saved);
      telemetry.track("transcript.updated", { transcriptId });
      return _success(saved);
    },

    // Moves it to Recently deleted; the server erases it after 7 days.
    delete: async (
      transcriptId: string,
      options?: ApiCallOptions,
    ): Promise<ActionResult<void>> => {
      const result = await api.transcript.delete(transcriptId, options);
      if ("error" in result) return _failure(result.error);
      _removeTranscript(store, transcriptId);
      telemetry.track("transcript.deleted", { transcriptId });
      return _success(undefined);
    },

    // Erases now: "Delete now" in Recently deleted, and discarded recordings
    // that should never land there.
    deleteForever: async (
      transcriptId: string,
      options?: ApiCallOptions,
    ): Promise<ActionResult<void>> => {
      const result = await api.transcript.deleteForever(transcriptId, options);
      if ("error" in result) return _failure(result.error);
      _removeTranscript(store, transcriptId);
      telemetry.track("transcript.deleted", { transcriptId });
      return _success(undefined);
    },

    listDeleted: async (
      nextCursor?: string,
      options?: ApiCallOptions,
    ): Promise<ActionResult<{ transcripts: Transcript[]; nextCursor: string }>> => {
      const result = await api.transcript.listDeleted(nextCursor, options);
      if ("error" in result) return _failure(result.error);
      return _success({
        transcripts: result.value?.transcripts || [],
        nextCursor: result.value?.nextCursor || "",
      });
    },

    restore: async (
      transcriptId: string,
      options?: ApiCallOptions,
    ): Promise<ActionResult<Transcript>> => {
      const result = await api.transcript.restore(transcriptId, options);
      if ("error" in result) return _failure(result.error);
      const restored = result.value?.transcript;
      if (!restored) return _failure("invalidResponse");
      _reconcileTranscript(store, restored);
      // The server put it back in its tags; the tag list lives on the user.
      const user = await api.user.get(options);
      if (!("error" in user) && user.value?.user) {
        store.setState({ userData: user.value.user });
      }
      telemetry.track("transcript.restored", { transcriptId });
      return _success(restored);
    },

    getUploadData: async (
      fileName: string,
      options?: ApiCallOptions,
    ): Promise<ActionResult<{ uploadUrl: string; downloadUrl: string }>> => {
      const result = await api.transcript.getUploadData(fileName, options);
      if ("error" in result) return _failure(result.error);
      const { uploadUrl, downloadUrl } = result.value || {};
      if (!uploadUrl || !downloadUrl) return _failure("invalidResponse");
      return _success({ uploadUrl, downloadUrl });
    },

    createMultipartUpload: async (
      fileName: string,
      fileSize: number,
      options?: ApiCallOptions,
    ): Promise<
      ActionResult<{
        objectKey: string;
        uploadId: string;
        downloadUrl: string;
        partSize: number;
        partCount: number;
      }>
    > => {
      const result = await api.transcript.createMultipartUpload(
        fileName,
        fileSize,
        options,
      );
      if ("error" in result) return _failure(result.error);
      const { objectKey, uploadId, downloadUrl, partSize, partCount } =
        result.value || {};
      if (!objectKey || !uploadId || !downloadUrl || !partSize || !partCount)
        return _failure("invalidResponse");
      return _success({
        objectKey,
        uploadId,
        downloadUrl,
        partSize,
        partCount,
      });
    },

    getMultipartUploadPartUrl: async (
      input: {
        objectKey: string;
        uploadId: string;
        partNumber: number;
        fileSize: number;
      },
      options?: ApiCallOptions,
    ): Promise<ActionResult<{ uploadUrl: string }>> => {
      const result = await api.transcript.getMultipartUploadPartUrl(
        input,
        options,
      );
      if ("error" in result) return _failure(result.error);
      const { uploadUrl } = result.value || {};
      return uploadUrl ? _success({ uploadUrl }) : _failure("invalidResponse");
    },

    completeMultipartUpload: async (
      input: { objectKey: string; uploadId: string; fileSize: number },
      options?: ApiCallOptions,
    ): Promise<ActionResult<{ downloadUrl: string }>> => {
      const result = await api.transcript.completeMultipartUpload(
        input,
        options,
      );
      if ("error" in result) return _failure(result.error);
      const { downloadUrl } = result.value || {};
      return downloadUrl
        ? _success({ downloadUrl })
        : _failure("invalidResponse");
    },

    abortMultipartUpload: async (
      input: { objectKey: string; uploadId: string },
      options?: ApiCallOptions,
    ): Promise<ActionResult<void>> => {
      const result = await api.transcript.abortMultipartUpload(input, options);
      return "error" in result ? _failure(result.error) : _success(undefined);
    },

    transcribeFullAudio: async (
      transcriptId: string,
      options?: ApiCallOptions,
    ): Promise<ActionResult<Transcript | undefined>> => {
      const current = store
        .getState()
        .transcripts.find((item) => item.id === transcriptId);
      if (!current) return _failure("TranscriptNotFound");
      if (current.isFullTranscript && !_isFreeDraft(current)) {
        return _success(current);
      }

      const result = await api.transcript.transcribeFullAudio(
        transcriptId,
        options,
      );
      if ("error" in result) return _failure(result.error);
      if (result.value?.transcript) {
        _replaceTranscript(store, transcriptId, result.value.transcript);
      }
      telemetry.track("transcript.processingStarted", {
        transcriptId,
        operation: "fullAudio",
      });
      return _success(result.value?.transcript);
    },

    // A store purchase reaches the server through a RevenueCat webhook a few
    // seconds after the app sees it, so the server can still refuse the full
    // transcript at first. Retry briefly; the "Transcribe full" button stays
    // as the fallback when the webhook is slower than the window.
    transcribeFullAudioAfterPurchase: async (
      transcriptId: string,
      options?: ApiCallOptions,
    ): Promise<ActionResult<Transcript | undefined>> => {
      // A complete free draft is redone only from its explicit "Redo with
      // Premium" button: an automatic re-run would replace the user's edits
      // and AI content without asking.
      const current = store
        .getState()
        .transcripts.find((item) => item.id === transcriptId);
      if (current?.isFullTranscript) return _success(current);
      const startedAt = clock.now();
      while (true) {
        const result = await transcript.transcribeFullAudio(
          transcriptId,
          options,
        );
        if (!("error" in result)) return result;
        if (result.error !== "NotAllowedToTranscribeFullAudio") return result;
        const remainingMs =
          _checkoutEntitlementTimeoutMs - (clock.now() - startedAt);
        if (remainingMs <= 0 || options?.signal?.aborted) return result;
        await _wait(
          clock,
          Math.min(_checkoutEntitlementPollIntervalMs, remainingMs),
        );
      }
    },

    addSpeaker: async (
      transcriptId: string,
      options?: ApiCallOptions,
    ): Promise<ActionResult<Transcript | undefined>> => {
      if (!store.getState().isSubscribed) return _failure("needsPremium");
      return _startTranscriptOperation(
        store,
        telemetry,
        transcriptId,
        "speakerLabels",
        () => api.transcript.addSpeaker(transcriptId, options),
      );
    },

    liveRecordingChunk: async (
      params: {
        transcriptId: string;
        action: "setFormat" | "addChunk" | "finalize" | "status" | "stopped";
        format?: {
          deviceType: User["deviceType"];
          sampleRate: number;
          channels: number;
          bitsPerSample: number;
        };
        chunk?: number[];
        sequenceNumber?: number;
        finalSequenceNumber?: number;
      },
      options?: ApiCallOptions,
    ): Promise<ActionResult<LiveRecordingStatus | undefined>> => {
      if (params.action === "finalize") {
        const saved = await pendingRecordingLanguageSaves.get(
          params.transcriptId,
        );
        if (saved && "error" in saved) return _failure(saved.error);
      }
      const request =
        params.action === "setFormat"
          ? {
              type: "liveRecordingChunk" as const,
              payload: {
                action: "setFormat" as const,
                transcriptId: params.transcriptId,
                format: params.format,
              },
            }
          : params.action === "addChunk"
            ? {
                type: "liveRecordingChunk" as const,
                payload: {
                  action: "addChunk" as const,
                  transcriptId: params.transcriptId,
                  chunk: params.chunk,
                  sequenceNumber: params.sequenceNumber,
                },
              }
            : params.action === "stopped"
              ? { type: "liveRecordingChunk" as const, payload: { action: "stopped" as const, transcriptId: params.transcriptId, finalSequenceNumber: params.finalSequenceNumber! } }
              : {
                type: "liveRecordingChunk" as const,
                payload: {
                  action: params.action,
                  ...(params.finalSequenceNumber !== undefined ? { finalSequenceNumber: params.finalSequenceNumber } : {}),
                  transcriptId: params.transcriptId,
                },
              };
      const result = await api.transcript.liveRecordingChunk(request, options);
      if ("error" in result) return _failure(result.error);

      if (params.action === "finalize") store.setState((state) => ({
        transcripts: state.transcripts.map((item) =>
          item.id === params.transcriptId
            ? { ...item, status: "transcription_started" }
            : item,
        ),
      }));
      return _success(result.value?.liveRecordingStatus);
    },

    rename: (
      transcriptId: string,
      fileName: string,
      options?: ApiCallOptions,
    ) => transcript.update(transcriptId, { fileName }, options),

    editSegment: (
      transcriptId: string,
      segment: Transcript["segments"][number],
      options?: ApiCallOptions,
    ) => {
      const current = store
        .getState()
        .transcripts.find((item) => item.id === transcriptId);
      if (!current) {
        return Promise.resolve(_failure<Transcript>("TranscriptNotFound"));
      }
      const segments = current.segments.map((item) =>
        item.start === segment.start ? segment : item,
      );
      return transcript.update(transcriptId, { segments }, options);
    },

    renameSpeaker: (
      transcriptId: string,
      previousSpeaker: string,
      nextSpeaker: string,
      options?: ApiCallOptions,
    ) => {
      const current = store
        .getState()
        .transcripts.find((item) => item.id === transcriptId);
      if (!current) {
        return Promise.resolve(_failure<Transcript>("TranscriptNotFound"));
      }
      const segments = current.segments.map((segment) =>
        segment.speaker === previousSpeaker
          ? { ...segment, speaker: nextSpeaker }
          : segment,
      );
      return transcript.update(transcriptId, { segments }, options);
    },

    pollStatus: (
      transcriptId: string,
      options: TranscriptPollOptions = {},
    ): TranscriptPollHandle => {
      const key = options.keySuffix
        ? `${transcriptId}:${options.keySuffix}`
        : transcriptId;
      const existing = activePolls.get(key);
      if (existing) return existing;

      const current = store
        .getState()
        .transcripts.find((item) => item.id === transcriptId);
      const isComplete =
        options.isComplete ||
        ((value: Transcript) =>
          !value.status || !_pendingTranscriptStatuses.has(value.status));
      if (current && isComplete(current)) {
        return {
          key,
          cancel: () => {},
          done: Promise.resolve(_success(current)),
        };
      }

      const startedAt = clock.now();
      const timeoutMs = options.timeoutMs ?? 3 * 60 * 60 * 1_000;
      let timer: TimerHandle | undefined;
      let requestController: AbortController | undefined;
      let finished = false;
      let resolveDone: (result: ActionResult<Transcript>) => void = () => {};
      const done = new Promise<ActionResult<Transcript>>((resolve) => {
        resolveDone = resolve;
      });

      const finish = (
        result: ActionResult<Transcript>,
        status: WorkHealth["status"],
      ) => {
        if (finished) return;
        finished = true;
        if (timer) clock.clearTimeout(timer);
        requestController?.abort();
        activePolls.delete(key);
        const finishedAt = clock.now();
        _setWorkHealth(store, key, {
          ...store.getState().workHealth[key],
          startedAt,
          lastProgressAt:
            store.getState().workHealth[key]?.lastProgressAt || startedAt,
          finishedAt,
          status,
          error: "error" in result ? result.error : undefined,
        });
        telemetry.track(
          status === "completed"
            ? "poll.completed"
            : status === "cancelled"
              ? "poll.cancelled"
              : "poll.failed",
          {
            transcriptId,
            error: "error" in result ? result.error : undefined,
          },
        );
        resolveDone(result);
      };

      const schedule = () => {
        if (finished) return;
        timer = clock.setTimeout(
          () => void run(),
          _pollIntervalMs(clock.now() - startedAt),
        );
      };

      const run = async () => {
        if (finished) return;
        if (clock.now() - startedAt >= timeoutMs) {
          finish(_failure("timeout"), "failed");
          return;
        }

        requestController = new AbortController();
        const result = await api.transcript.get(transcriptId, {
          signal: requestController.signal,
        });
        requestController = undefined;
        if (finished) return;
        if ("error" in result) {
          if (
            result.error === "networkUnavailable" ||
            result.error === "timeout" ||
            result.error === "cancelled"
          ) {
            schedule();
            return;
          }
          finish(_failure(result.error), "failed");
          return;
        }

        const received = result.value?.transcript;
        if (!received) {
          finish(_failure("invalidResponse"), "failed");
          return;
        }
        _reconcileTranscript(store, received, { skipUnchangedRevision: true });
        _setWorkHealth(store, key, {
          startedAt,
          lastProgressAt: clock.now(),
          status: "running",
        });
        if (isComplete(received)) {
          finish(_success(received), "completed");
        } else {
          schedule();
        }
      };

      const handle: TranscriptPollHandle = {
        key,
        done,
        cancel: () => finish(_failure("cancelled"), "cancelled"),
      };
      activePolls.set(key, handle);
      _setWorkHealth(store, key, {
        startedAt,
        lastProgressAt: startedAt,
        status: "running",
      });
      telemetry.track("poll.started", { transcriptId });
      schedule();
      return handle;
    },
  };

  const subscription = {
    configureRevenueCat: async (givenInput?: {
      appUserId?: string;
      email?: string;
      attributes?: Record<string, string>;
      apiKey: string;
      firebaseAppInstanceId?: string;
      enableAdServicesAttribution?: boolean;
    }): Promise<
      ActionResult<{
        packages: PurchasePackage[];
        customerInfo: PurchaseCustomerInfo;
      }>
    > => {
      const input =
        givenInput ||
        (await subscriptionEnvironment
          ?.nativeConfiguration()
          .catch(() => undefined));
      if (!input) return _failure("purchaseUnavailable");
      try {
        await revenueCat.configure({
          appUserId: input.appUserId,
          apiKey: input.apiKey,
        });
        if (input.appUserId) {
          try {
            await revenueCat.identify({
              appUserId: input.appUserId,
              email: input.email,
              attributes: input.attributes,
            });
          } catch {
            // Optional vendor identity enrichment must not hide offerings.
          }
        }
        if (input.firebaseAppInstanceId) {
          try {
            await revenueCat.setFirebaseAppInstanceId(
              input.firebaseAppInstanceId,
            );
          } catch {
            // Optional attribution must not hide offerings.
          }
        }
        const loaded = await revenueCat.load();
        store.setState({ nativePurchase: loaded });
        if (input.enableAdServicesAttribution) {
          try {
            await revenueCat.enableAdServicesAttribution();
          } catch {
            // Optional attribution must never make offerings or entitlement
            // state look unavailable.
          }
        }
        return _success(loaded);
      } catch {
        return _failure("purchaseFailed");
      }
    },

    revenueCatPurchase: async (
      packageIdentifier: string,
      options?: { upgradeFromProductIdentifier?: string },
    ) => {
      const result = await revenueCat.purchase(packageIdentifier, options);
      if ("error" in result) return _failure(result.error);
      const isSubscribed = result.value.activeSubscriptions.length > 0;
      if (!isSubscribed) return _failure("purchaseFailed");
      store.setState((state) => ({
        nativePurchase: state.nativePurchase
          ? { ...state.nativePurchase, customerInfo: result.value }
          : state.nativePurchase,
      }));
      store.setSubscribed(true);
      telemetry.track("subscription.statusChanged", { isSubscribed: true });
      return _success(result.value);
    },

    revenueCatRestorePurchase: async () => {
      const result = await revenueCat.restore();
      if ("error" in result) return _failure(result.error);
      const isSubscribed = result.value.activeSubscriptions.length > 0;
      if (!isSubscribed) return _failure("SubscriptionNotFound");
      store.setState((state) => ({
        nativePurchase: state.nativePurchase
          ? { ...state.nativePurchase, customerInfo: result.value }
          : state.nativePurchase,
      }));
      store.setSubscribed(true);
      telemetry.track("subscription.statusChanged", { isSubscribed: true });
      return _success(result.value);
    },

    status: async (
      options?: ApiCallOptions,
    ): Promise<ActionResult<boolean>> => {
      const result = await api.subscription.status(options);
      if ("error" in result) return _failure(result.error);
      if (typeof result.value?.isActive !== "boolean") {
        return _failure("invalidResponse");
      }
      store.setState({
        subscriptionManagementProvider: result.value.managementProvider,
      });
      store.setSubscribed(result.value.isActive);
      telemetry.track("subscription.statusChanged", {
        isSubscribed: result.value.isActive,
      });
      return _success(result.value.isActive);
    },

    stripeCheckout: async (
      input: {
        baseAppUrl?: string;
        subscriptionPlan: Parameters<ApiClient["subscription"]["checkout"]>[1];
        attribution?: Record<string, string>;
      },
      options?: ApiCallOptions,
    ): Promise<ActionResult<string>> => {
      if (store.getState().isSubscribed) {
        return _failure("AlreadySubscribed");
      }
      const baseAppUrl =
        input.baseAppUrl || subscriptionEnvironment?.baseAppUrl();
      if (!baseAppUrl) return _failure("InvalidInput");
      const attribution =
        input.attribution ?? subscriptionEnvironment?.attribution();
      const result = await api.subscription.checkout(
        baseAppUrl,
        input.subscriptionPlan,
        attribution,
        options,
      );
      if ("error" in result) return _failure(result.error);
      if (!result.value?.url) return _failure("invalidResponse");
      telemetry.track("subscription.checkoutStarted", {
        plan: input.subscriptionPlan,
      });
      return _success(result.value.url);
    },

    completeCheckout: async (
      input: { transcriptId?: string } = {},
      options?: ApiCallOptions,
    ): Promise<
      ActionResult<{
        isSubscribed: boolean;
        transcriptId?: string;
        transcriptionStarted: boolean;
      }>
    > => {
      const startedAt = clock.now();
      let isSubscribed = false;
      while (true) {
        const statusResult = await subscription.status(options);
        if ("error" in statusResult) {
          return _failure(statusResult.error);
        } else if (statusResult.value) {
          isSubscribed = true;
          break;
        }

        const remainingMs =
          _checkoutEntitlementTimeoutMs - (clock.now() - startedAt);
        if (remainingMs <= 0) break;
        await _wait(
          clock,
          Math.min(_checkoutEntitlementPollIntervalMs, remainingMs),
        );
      }

      let transcriptionStarted = false;
      if (isSubscribed && input.transcriptId) {
        let current = store
          .getState()
          .transcripts.find((item) => item.id === input.transcriptId);
        if (!current) {
          const loaded = await transcript.get(input.transcriptId, options);
          if ("error" in loaded) return _failure(loaded.error);
          current = loaded.value;
        }
        if (current && current.isFullTranscript === false) {
          const transcription = await transcript.transcribeFullAudio(
            input.transcriptId,
            options,
          );
          if ("error" in transcription) return _failure(transcription.error);
          transcriptionStarted = true;
        } else if (
          current?.isFullTranscript &&
          current.status === "transcription_started"
        ) {
          transcriptionStarted = true;
        }
      }

      return _success({
        isSubscribed,
        transcriptId: input.transcriptId,
        transcriptionStarted,
      });
    },

    stripeBillingPortal: async (
      baseAppUrl?: string,
      options?: ApiCallOptions,
    ): Promise<ActionResult<string>> => {
      const resolvedBaseAppUrl =
        baseAppUrl || subscriptionEnvironment?.baseAppUrl();
      if (!resolvedBaseAppUrl) return _failure("InvalidInput");
      const result = await api.subscription.billingPortal(
        resolvedBaseAppUrl,
        options,
      );
      if ("error" in result) return _failure(result.error);
      if (!result.value?.url) return _failure("invalidResponse");
      return _success(result.value.url);
    },

    stripeAnnualUpgradeOffer: async (
      options?: ApiCallOptions,
    ): Promise<ActionResult<SubscriptionOffer | undefined>> => {
      const result = await api.subscription.annualUpgradeOffer(options);
      if ("error" in result) return _failure(result.error);
      const offer = result.value?.annualUpgrade;
      if (!offer) return _success(undefined);
      if (
        offer.billingPeriod !== "year" ||
        !["low", "mid", "high"].includes(offer.tier) ||
        !["low_tier_annual", "mid_tier_annual", "top_tier_annual"].includes(
          offer.subscriptionPlan,
        ) ||
        offer.currency !== "USD" ||
        !Number.isInteger(offer.unitAmount) ||
        offer.unitAmount <= 0
      ) {
        return _failure("invalidResponse");
      }
      return _success(offer);
    },

    stripeAnnualUpgrade: async (
      baseAppUrl?: string,
      options?: ApiCallOptions,
    ): Promise<ActionResult<string>> => {
      const resolvedBaseAppUrl =
        baseAppUrl || subscriptionEnvironment?.baseAppUrl();
      if (!resolvedBaseAppUrl) return _failure("InvalidInput");
      const result = await api.subscription.annualUpgrade(
        resolvedBaseAppUrl,
        options,
      );
      if ("error" in result) return _failure(result.error);
      return result.value?.url
        ? _success(result.value.url)
        : _failure("invalidResponse");
    },

    manage: async (options?: ApiCallOptions): Promise<ActionResult<string>> => {
      const provider = store.getState().subscriptionManagementProvider;
      if (!provider) return _failure("SubscriptionNotFound");
      if (provider === "stripe") {
        return subscription.stripeBillingPortal(undefined, options);
      }
      const url = subscriptionEnvironment?.managementUrl(provider);
      return url ? _success(url) : _failure("InvalidInput");
    },
  };

  const user = {
    delete: async (options?: ApiCallOptions): Promise<ActionResult<void>> => {
      const result = await api.user.delete(options);
      if ("error" in result) return _failure(result.error);
      for (const poll of activePolls.values()) poll.cancel();
      return _success(undefined);
    },

    setAiConsent: async (accepted: boolean, options?: ApiCallOptions): Promise<ActionResult<User>> => {
      const token = store.getState().userToken;
      const result = await api.user.setAiConsent(accepted, options);
      if ("error" in result) return _failure(result.error);
      if (store.getState().userToken !== token) return _failure("cancelled");
      if (!result.value?.user) return _failure("invalidResponse");
      store.setState({ userData: result.value.user });
      return _success(result.value.user);
    },

    get: async (options?: ApiCallOptions): Promise<ActionResult<User>> => {
      const result = await api.user.get(options);
      if ("error" in result) return _failure(result.error);
      const received = result.value?.user;
      if (!received) return _failure("invalidResponse");
      store.setState({ userData: received });
      telemetry.track("user.loaded", { userId: received.id });
      return _success(received);
    },

    update: async (
      updates: Partial<User>,
      options?: ApiCallOptions,
    ): Promise<ActionResult<User>> => {
      const previous = store.getState().userData;
      const optimistic = { ...previous, ...updates };
      store.setState({ userData: optimistic });
      const result = await api.user.update(updates, options);
      if ("error" in result) {
        if (store.getState().userData === optimistic) {
          store.setState({ userData: previous });
        }
        return _failure(result.error);
      }
      const saved = result.value?.user;
      if (!saved) {
        if (store.getState().userData === optimistic) {
          store.setState({ userData: previous });
        }
        return _failure("invalidResponse");
      }
      // A newer optimistic update may already be visible. An older response
      // must not roll that choice back merely because the network completed
      // out of order.
      if (store.getState().userData === optimistic) {
        store.setState({ userData: saved });
      }
      telemetry.track("user.updated", { userId: saved.id });
      return _success(saved);
    },

    captureAcquisition: async (
      attribution: Record<string, string>,
      options?: ApiCallOptions,
    ): Promise<ActionResult<void>> => {
      const result = await api.user.captureAcquisition(attribution, options);
      return "error" in result ? _failure(result.error) : _success(undefined);
    },

    desktopAuthorize: async (
      challenge: string,
      options?: ApiCallOptions,
    ): Promise<ActionResult<string>> => {
      const result = await api.user.desktopAuthorize(challenge, options);
      if ("error" in result) return _failure(result.error);
      return result.value?.code
        ? _success(result.value.code)
        : _failure("invalidResponse");
    },

    changeTranscriptionLanguage: async (
      language: string,
      options?: ApiCallOptions,
      recordingTranscriptId?: string,
    ): Promise<ActionResult<User>> => {
      const previousSave = recordingTranscriptId
        ? pendingRecordingLanguageSaves.get(recordingTranscriptId)
        : undefined;
      const save = (async (): Promise<ActionResult<User>> => {
        // Keep rapid language changes in order, including the saved default.
        if (previousSave) await previousSave;
        // A live recording already has its own transcript. Persist its language
        // before changing the default, so recovery uses the selected language too.
        if (recordingTranscriptId) {
          const result = await transcript.update(
            recordingTranscriptId,
            { language },
            options,
          );
          if ("error" in result) return _failure(result.error);
        }
        return user.update(
          {
            preferences: {
              ...store.getState().userData?.preferences,
              transcriptionLanguage: language,
            },
          },
          options,
        );
      })();
      if (recordingTranscriptId)
        pendingRecordingLanguageSaves.set(recordingTranscriptId, save);
      try {
        return await save;
      } finally {
        if (
          recordingTranscriptId &&
          pendingRecordingLanguageSaves.get(recordingTranscriptId) === save
        )
          pendingRecordingLanguageSaves.delete(recordingTranscriptId);
      }
    },

    changeDictationLanguage: (
      language: string | undefined,
      options?: ApiCallOptions,
    ) => {
      const preferences = {
        ...store.getState().userData?.preferences,
      };
      if (language === undefined) delete preferences.dictationLanguage;
      else {
        preferences.dictationLanguage = language;
        preferences.recentDictationLanguages = _promoteRecentDictationLanguage(
          preferences.recentDictationLanguages,
          language,
        );
      }
      const save = user.update({ preferences }, options);
      pendingDictationLanguageSave = save;
      return save;
    },

    changeParagraphSentenceLimit: (
      paragraphSentenceLimit: ParagraphSentenceLimit | undefined,
      options?: ApiCallOptions,
    ) => {
      const preferences = {
        ...store.getState().userData?.preferences,
      };
      if (paragraphSentenceLimit === undefined) {
        delete preferences.paragraphSentenceLimit;
      } else {
        preferences.paragraphSentenceLimit = paragraphSentenceLimit;
      }
      return user.update({ preferences }, options);
    },

    remoteConfig: async (
      query: { platform?: string; version?: string } = {},
      options?: ApiCallOptions,
    ): Promise<ActionResult<RemoteConfig>> => {
      const result = await api.remoteConfig.get(query, options);
      if ("error" in result) return _failure(result.error);
      store.setState({
        remoteConfig: result.value,
        country: result.value.country,
        webSubscriptionPlans: result.value.webPlans,
      });
      return _success(result.value);
    },

    requestWebSignIn: async (
      email: string,
      options?: ApiCallOptions,
    ): Promise<ActionResult<void>> => {
      const result = await api.user.requestWebSignIn(email, options);
      return "error" in result ? _failure(result.error) : _success(undefined);
    },

    confirmWebSignIn: async (
      oneTimeCode: number,
      options?: ApiCallOptions,
    ): Promise<ActionResult<string>> => {
      const result = await api.user.confirmWebSignIn(oneTimeCode, options);
      if ("error" in result) return _failure(result.error);
      const accessToken = result.value?.accessToken;
      if (!accessToken) return _failure("invalidResponse");
      return _success(accessToken);
    },

    webSignInWithGoogle: async (
      googleAccessToken: string,
      options?: ApiCallOptions,
    ): Promise<ActionResult<string>> => {
      const result = await api.user.webSignInWithGoogle(
        googleAccessToken,
        options,
      );
      if ("error" in result) return _failure(result.error);
      const accessToken = result.value?.accessToken;
      if (!accessToken) return _failure("invalidResponse");
      return _success(accessToken);
    },

    requestNativeSignIn: async (
      email: string,
      options?: ApiCallOptions,
    ): Promise<ActionResult<void>> => {
      const result = await api.user.requestNativeSignIn(email, options);
      return "error" in result ? _failure(result.error) : _success(undefined);
    },

    confirmNativeSignIn: async (
      oneTimeCode: number,
      options?: ApiCallOptions,
    ): Promise<ActionResult<string>> => {
      const result = await api.user.confirmNativeSignIn(oneTimeCode, options);
      if ("error" in result) return _failure(result.error);
      const accessToken = result.value?.accessToken;
      if (!accessToken) return _failure("invalidResponse");
      const started = await _startSession(accessToken);
      return "error" in started
        ? _failure(started.error)
        : _success(accessToken);
    },

    profile: async (): Promise<ActionResult<void>> => {
      const results = await Promise.all([
        user.get(),
        transcript.list(),
        subscription.status(),
      ]);
      const failed = results.find(
        (result): result is { ok: false; error: ActionError } =>
          "error" in result,
      );
      return failed ? _failure(failed.error) : _success(undefined);
    },
  };

  let consentAttempt: Promise<ActionResult<void>> | undefined;
  const ensureAiConsent = async (options?: ApiCallOptions): Promise<ActionResult<void>> => {
    if (options?.signal?.aborted) return _failure("cancelled");
    if (!dependencies.requestAiConsent) return _success(undefined);
    if (store.getState().userData?.aiConsent?.version === 1) return _success(undefined);
    if (!consentAttempt) {
      const token = store.getState().userToken;
      consentAttempt = (async (): Promise<ActionResult<void>> => {
        // Fetch the account decision, including consent saved on another device.
        const profile = await api.user.get(options);
        if ("error" in profile) return _failure(profile.error);
        if (store.getState().userToken !== token || options?.signal?.aborted) return _failure("cancelled");
        if (!profile.value?.user) return _failure("invalidResponse");
        store.setState({ userData: profile.value.user });
        if (profile.value.user.aiConsent?.version === 1) return _success(undefined);
        if (!await dependencies.requestAiConsent?.(options?.signal)) return _failure("cancelled");
        if (store.getState().userToken !== token || options?.signal?.aborted) return _failure("cancelled");
        const saved = await user.setAiConsent(true, options);
        return "error" in saved ? _failure(saved.error) : _success(undefined);
      })();
    }
    const attempt = consentAttempt;
    try {
      const result = await attempt;
      return options?.signal?.aborted ? _failure("cancelled") : result;
    } finally {
      if (consentAttempt === attempt) consentAttempt = undefined;
    }
  };

  const requestAiContent = async (
    transcriptId: string,
    feature: "translation" | "summary" | "flashcard" | "quiz",
    request: Parameters<typeof _requestAiContent>[4],
    options?: ApiCallOptions,
  ): Promise<ActionResult<void>> => {
    const consent = await ensureAiConsent(options);
    if ("error" in consent) return consent;
    return _requestAiContent(store, telemetry, transcriptId, feature, request);
  };

  const aiContent = {
    addTranslation: (
      transcriptId: string,
      language: string,
      options?: ApiCallOptions,
    ) =>
      requestAiContent(transcriptId, "translation", () =>
        api.aiContent.addTranslation(transcriptId, language, options), options,
      ),
    addSummary: (
      transcriptId: string,
      instructions?: string,
      options?: ApiCallOptions,
    ) =>
      requestAiContent(transcriptId, "summary", () =>
        api.aiContent.addSummary(transcriptId, instructions, options), options,
      ),
    addFlashcard: (
      transcriptId: string,
      instructions?: string,
      options?: ApiCallOptions,
    ) =>
      requestAiContent(transcriptId, "flashcard", () =>
        api.aiContent.addFlashcard(transcriptId, instructions, options), options,
      ),
    addQuiz: (
      transcriptId: string,
      instructions?: string,
      options?: ApiCallOptions,
    ) =>
      requestAiContent(transcriptId, "quiz", () =>
        api.aiContent.addQuiz(transcriptId, instructions, options), options,
      ),
    generate: (
      input: AiContentGenerationInput,
      options: { timeoutMs?: number } = {},
    ): TranscriptPollHandle => {
      const suffix =
        input.feature === "translation"
          ? `translation:${input.language}`
          : input.feature;
      const key = `${input.transcriptId}:${suffix}`;
      const requestController = new AbortController();
      let poll: TranscriptPollHandle | undefined;
      let finished = false;
      let resolveDone: (result: ActionResult<Transcript>) => void = () => {};
      const done = new Promise<ActionResult<Transcript>>((resolve) => {
        resolveDone = resolve;
      });
      const finish = (result: ActionResult<Transcript>) => {
        if (finished) return;
        finished = true;
        resolveDone(result);
      };
      const isComplete = (value: Transcript) => {
        switch (input.feature) {
          case "translation":
            return Boolean(value.translation?.[input.language]);
          case "summary":
            return Boolean(value.summary);
          case "flashcard":
            return Boolean(value.flashcard?.length);
          case "quiz":
            return Boolean(value.quiz);
        }
      };

      void (async () => {
        const requestOptions = { signal: requestController.signal };
        const requested =
          input.feature === "translation"
            ? await aiContent.addTranslation(
                input.transcriptId,
                input.language,
                requestOptions,
              )
            : input.feature === "summary"
              ? await aiContent.addSummary(
                  input.transcriptId,
                  input.instructions,
                  requestOptions,
                )
              : input.feature === "flashcard"
                ? await aiContent.addFlashcard(
                    input.transcriptId,
                    input.instructions,
                    requestOptions,
                  )
                : await aiContent.addQuiz(
                    input.transcriptId,
                    input.instructions,
                    requestOptions,
                  );
        if (finished) return;
        if ("error" in requested) {
          finish(_failure(requested.error));
          return;
        }

        poll = transcript.pollStatus(input.transcriptId, {
          keySuffix: suffix,
          timeoutMs: options.timeoutMs ?? 10 * 60 * 1_000,
          isComplete,
        });
        const completed = await poll.done;
        if (finished) return;
        if (!("error" in completed)) {
          telemetry.track("aiContent.completed", {
            transcriptId: input.transcriptId,
            feature: input.feature,
          });
        }
        finish(completed);
      })();

      return {
        key,
        done,
        cancel: () => {
          if (finished) return;
          requestController.abort();
          poll?.cancel();
          finish(_failure("cancelled"));
        },
      };
    },
    markdownToPdf: async (
      transcriptId: string,
      htmlString: string,
      options?: ApiCallOptions,
    ): Promise<ActionResult<string>> => {
      const result = await api.aiContent.markdownToPdf(
        { type: "markdownToPdf", transcriptId, htmlString },
        options,
      );
      if ("error" in result) return _failure(result.error);
      if (!result.value?.pdfBase64) return _failure("invalidResponse");
      return _success(result.value.pdfBase64);
    },
  };

  const tags = {
    getAll: (): Tag[] => (store.getState().userData?.allTags as Tag[]) || [],

    getByName: (name: string): Tag | undefined =>
      tags
        .getAll()
        .find((tag) => tag.name.toLowerCase() === name.toLowerCase()),

    getTagsForTranscript: (transcriptId: string): Tag[] => {
      const value = store
        .getState()
        .transcripts.find((item) => item.id === transcriptId);
      if (!value?.tagsIds?.length) return [];
      const allTags = tags.getAll();
      return value.tagsIds
        .map((name) => allTags.find((tag) => tag.name === name))
        .filter((tag): tag is Tag => Boolean(tag));
    },

    create: async (name: string): Promise<ActionResult<Tag>> => {
      const trimmed = name.trim();
      if (!trimmed) return _failure("InvalidInput");
      const existing = tags.getByName(trimmed);
      if (existing) return _success(existing);
      const created: Tag = {
        name: trimmed,
        color: _tagColor(trimmed),
        createdAt: new Date(clock.now()).toISOString(),
        notesIds: [],
      };
      const result = await user.update({
        allTags: [created, ...tags.getAll()],
      });
      return "error" in result ? _failure(result.error) : _success(created);
    },

    addToTranscripts: async (
      transcriptIds: string[],
      tagNames: string[],
    ): Promise<ActionResult<void>> => {
      const updatedTags = tags.getAll().map((tag) => {
        if (!tagNames.includes(tag.name)) return tag;
        const currentIds = tag.notesIds || [];
        return {
          ...tag,
          notesIds: [
            ...transcriptIds.filter((id) => !currentIds.includes(id)),
            ...currentIds,
          ],
        };
      });
      const userResult = await user.update({ allTags: updatedTags });
      if ("error" in userResult) return _failure(userResult.error);

      for (const transcriptId of transcriptIds) {
        const current = store
          .getState()
          .transcripts.find((item) => item.id === transcriptId);
        if (!current) continue;
        const existing = current.tagsIds || [];
        const result = await transcript.update(transcriptId, {
          tagsIds: [
            ...existing,
            ...tagNames.filter((tag) => !existing.includes(tag)),
          ],
        });
        if ("error" in result) return _failure(result.error);
      }
      return _success(undefined);
    },

    removeFromTranscript: async (
      transcriptId: string,
      tagName: string,
    ): Promise<ActionResult<void>> => {
      const current = store
        .getState()
        .transcripts.find((item) => item.id === transcriptId);
      if (!current) return _failure("TranscriptNotFound");
      const transcriptResult = await transcript.update(transcriptId, {
        tagsIds: (current.tagsIds || []).filter((name) => name !== tagName),
      });
      if ("error" in transcriptResult) return _failure(transcriptResult.error);

      const existingTag = tags.getByName(tagName);
      if (!existingTag) return _success(undefined);
      const notesIds = (existingTag.notesIds || []).filter(
        (id) => id !== transcriptId,
      );
      const allTags =
        notesIds.length === 0
          ? tags.getAll().filter((tag) => tag.name !== tagName)
          : tags
              .getAll()
              .map((tag) =>
                tag.name === tagName ? { ...tag, notesIds } : tag,
              );
      const userResult = await user.update({ allTags });
      return "error" in userResult
        ? _failure(userResult.error)
        : _success(undefined);
    },

    fetchMissingTranscriptsForTag: async (
      tagName: string,
    ): Promise<ActionResult<Transcript[]>> => {
      const tag = tags.getByName(tagName);
      if (!tag?.notesIds?.length) return _success([]);
      const localIds = new Set(
        store.getState().transcripts.map((item) => item.id),
      );
      const missing = tag.notesIds.filter((id) => !localIds.has(id));
      const results = await Promise.all(
        missing.map((id) => transcript.get(id)),
      );
      const failed = results.find(
        (result): result is { ok: false; error: ActionError } =>
          "error" in result,
      );
      if (failed) return _failure(failed.error);
      return _success(
        results
          .filter(
            (result): result is { ok: true; value: Transcript } =>
              !("error" in result),
          )
          .map((result) => result.value),
      );
    },
  };

  // Local JWT payload read (this module must stay runtime-import-free).
  // Mirrors core.decodeJwt's UTF-8-safe base64url handling.
  const _sessionUserId = (token: string | undefined): string | undefined => {
    if (!token) return undefined;
    try {
      const base64Url = token.split(".")[1];
      if (!base64Url) return undefined;
      const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
      const jsonPayload = decodeURIComponent(
        globalThis
          .atob(base64)
          .split("")
          .map(
            (character) =>
              `%${`00${character.charCodeAt(0).toString(16)}`.slice(-2)}`,
          )
          .join(""),
      );
      const parsed: unknown = JSON.parse(jsonPayload);
      const userId =
        parsed && typeof parsed === "object"
          ? (parsed as { userId?: unknown }).userId
          : undefined;
      return typeof userId === "string" ? userId : undefined;
    } catch {
      return undefined;
    }
  };

  const _startSession = async (
    accessToken: string,
  ): Promise<ActionResult<void>> => {
    if (!accessToken) return _failure("InvalidAccessToken");
    const nextUserId = _sessionUserId(accessToken);
    if (
      store.getState().userToken === accessToken &&
      store.getState()._sessionExpiredAt === null
    ) {
      if (nextUserId && store.getState().deviceId !== nextUserId) {
        await store.setState({ deviceId: nextUserId });
      }
      return _success(undefined);
    }
    const previousUserId = _sessionUserId(store.getState().userToken);
    await store.setState({
      userToken: accessToken,
      _sessionExpiredAt: null,
      ...(nextUserId ? { deviceId: nextUserId } : {}),
    });
    telemetry.track("session.started");
    if (previousUserId && nextUserId && previousUserId !== nextUserId) {
      // Signing a live session into a different identity (guest → account)
      // changes who owns the remote data. Refresh it in the background so
      // the transcript list and settings reflect the new account without
      // an app restart.
      void user.profile();
    }
    return _success(undefined);
  };

  const _authenticatedSession = async (
    request: () => Promise<ClientResult<{ accessToken?: string } | undefined>>,
  ): Promise<ActionResult<string>> => {
    const result = await request();
    if ("error" in result) return _failure(result.error);
    const accessToken = result.value?.accessToken;
    if (!accessToken) return _failure("invalidResponse");
    const started = await _startSession(accessToken);
    return "error" in started ? _failure(started.error) : _success(accessToken);
  };

  const session = {
    start: _startSession,
    signInAsGuest: (
      input: {
        deviceId: string;
        deviceLanguage: string;
        deviceTimezone: string;
        deviceType: User["deviceType"];
      },
      options?: ApiCallOptions,
    ) => _authenticatedSession(() => api.auth.guest(input, options)),
    signInWithGoogle: (
      input: {
        googleAccessToken: string;
        deviceLanguage: string;
        deviceTimezone: string;
        deviceType: User["deviceType"];
      },
      options?: ApiCallOptions,
    ) => _authenticatedSession(() => api.auth.google(input, options)),
    requestOneTimeCode: async (
      input: {
        email: string;
        deviceLanguage: string;
        deviceTimezone: string;
        deviceType: User["deviceType"];
      },
      options?: ApiCallOptions,
    ): Promise<ActionResult<void>> => {
      const result = await api.auth.requestOneTimeCode(input, options);
      return "error" in result ? _failure(result.error) : _success(undefined);
    },
    confirmOneTimeCode: (
      input: { email: string; oneTimeCode: number },
      options?: ApiCallOptions,
    ) =>
      _authenticatedSession(() => api.auth.confirmOneTimeCode(input, options)),
    exchangeDesktopCode: (
      input: { code: string; verifier: string },
      options?: ApiCallOptions,
    ) => _authenticatedSession(() => api.auth.exchangeDesktopCode(input, options)),
    end: async (nextDeviceId?: string): Promise<ActionResult<void>> => {
      await store.setState({
        userToken: undefined,
        userData: undefined,
        isSubscribed: false,
        transcripts: [],
        transcriptsNextCursor: undefined,
        _sessionExpiredAt: null,
        ...(nextDeviceId ? { deviceId: nextDeviceId } : {}),
      });
      telemetry.track("session.ended");
      return _success(undefined);
    },
  };

  const dictation = {
    transcribe: async (
      input: {
        audio: Blob;
        fileName?: string;
        durationMs?: number;
        platform: DictationClientPlatform;
        trigger: DictationTrigger;
      },
      options?: ApiCallOptions,
    ): Promise<
      ActionResult<
        | {
            text: string;
            language?: string;
            duration?: number;
            paywallRequired?: false;
          }
        | { paywallRequired: true }
      >
    > => {
      const languageSave = pendingDictationLanguageSave;
      if (languageSave) {
        const languageResult = await languageSave;
        if (pendingDictationLanguageSave === languageSave) {
          pendingDictationLanguageSave = undefined;
        }
        if ("error" in languageResult) return _failure(languageResult.error);
      }
      const result = await api.dictation.transcribe(input, options);
      if ("error" in result) return _failure(result.error);
      if (result.value?.paywallRequired === true) {
        return _success({ paywallRequired: true });
      }
      if (
        typeof result.value?.text !== "string" ||
        result.value.text.trim().length === 0
      ) {
        return _failure("invalidResponse");
      }
      return _success({
        text: result.value.text,
        ...(typeof result.value.language === "string"
          ? { language: result.value.language }
          : {}),
        ...(typeof result.value.duration === "number"
          ? { duration: result.value.duration }
          : {}),
      });
    },
    rewrite: async (
      input: {
        text: string;
        action: "cleanUp" | "simplify";
        platform: DictationClientPlatform;
      },
      options?: ApiCallOptions,
    ): Promise<ActionResult<{ text: string }>> => {
      const consent = await ensureAiConsent(options);
      if ("error" in consent) return consent;
      const result = await api.dictation.rewrite(input, options);
      if ("error" in result) return _failure(result.error);
      const text = result.value?.text?.trim();
      return text ? _success({ text }) : _failure("invalidResponse");
    },
    outcome: async (
      input: {
        platform: DictationClientPlatform;
        trigger: DictationTrigger;
        delivery: DictationDelivery;
      },
      options?: ApiCallOptions,
    ): Promise<ActionResult<void>> => {
      const result = await api.dictation.outcome(input, options);
      return "error" in result ? _failure(result.error) : _success(undefined);
    },
  };

  const search = {
    all: async (
      query: string,
      options: ApiCallOptions & { maxPages?: number } = {},
    ): Promise<ActionResult<SearchResultItem[]>> => {
      const normalizedQuery = query.trim();
      if (!normalizedQuery) return _failure("InvalidInput");
      const { maxPages = 25, ...apiOptions } = options;
      const matches: SearchResultItem[] = [];
      let nextToken: string | undefined;
      let itemsProcessed = 0;

      for (let page = 0; page < maxPages; page += 1) {
        const result = await api.search.query(
          { query: normalizedQuery, nextToken, itemsProcessed },
          apiOptions,
        );
        if ("error" in result) return _failure(result.error);
        if (!result.value || !Array.isArray(result.value.matches)) {
          return _failure("invalidResponse");
        }
        matches.push(...result.value.matches);
        nextToken = result.value.nextToken || undefined;
        itemsProcessed = result.value.itemsProcessed ?? itemsProcessed;
        if (!nextToken) return _success(matches);
      }

      return _success(matches);
    },
  };

  const app = {
    requestTranscriptionReview: async (transcriptId: string): Promise<ActionResult<{ requested: boolean }>> => {
      const state = store.getState();
      const completed = state.transcripts.find((item) => item.id === transcriptId);
      if (!dependencies.requestReview || !state.hasCompletedOnboarding || state.postTranscriptionReviewRequested ||
          completed?.status !== "transcription_completed" || !completed.segments?.some((segment) => segment.text.trim())) {
        return _success({ requested: false });
      }
      // Persist before asking: navigation, remounts and concurrent callers must
      // never repeat this request. The OS decides whether to display it.
      await store.setState({ postTranscriptionReviewRequested: true });
      try {
        await dependencies.requestReview();
      } catch {
        // An optional store-rating prompt must not interrupt the transcript.
      }
      return _success({ requested: true });
    },

    recordOpen: (
      source: "launch" | "foreground",
    ): ActionResult<{ firstLaunch: boolean; openedAt: number }> => {
      const openedAt = clock.now();
      const firstLaunch = !store.getState().firstLaunchAt;
      store.setState({
        ...(firstLaunch ? { firstLaunchAt: openedAt } : {}),
        lastAppOpenAt: openedAt,
      });
      telemetry.track("app.opened", { source, firstLaunch });
      return _success({ firstLaunch, openedAt });
    },
  };

  const agent = {
    cancelConnection: async (connectionId: string, error: "access_denied" | "server_error"): Promise<ActionResult<string>> => {
      const result = await api.agent.cancelConnection(connectionId, error);
      if ("error" in result) return _failure(result.error);
      return result.value?.redirectUrl ? _success(result.value.redirectUrl) : _failure("invalidResponse");
    },
    describeConnection: async (connectionId: string): Promise<ActionResult<string>> => {
      const result = await api.agent.describeConnection(connectionId);
      if ("error" in result) return _failure(result.error);
      return typeof result.value?.assistant === "string" ? _success(result.value.assistant) : _failure("invalidResponse");
    },
    authorize: async (connectionId: string): Promise<ActionResult<string>> => {
      const result = await api.agent.authorize(connectionId);
      if ("error" in result) return _failure(result.error);
      return result.value?.redirectUrl ? _success(result.value.redirectUrl) : _failure("invalidResponse");
    },
    listConnections: async (): Promise<ActionResult<AgentConnectionSummary[]>> => {
      const connections = new Map<string, AgentConnectionSummary>();
      const cursors = new Set<string>();
      const deadline = clock.now() + 30_000;
      let nextCursor: string | undefined;
      do {
        const timeoutMs = deadline - clock.now();
        if (timeoutMs <= 0) return _failure("timeout");
        const result = await api.agent.listConnections(nextCursor, { timeoutMs });
        if ("error" in result) return _failure(result.error);
        if (!Array.isArray(result.value?.connections)) return _failure("invalidResponse");
        for (const connection of result.value.connections) connections.set(connection.id, connection);
        nextCursor = result.value.nextCursor;
        if (nextCursor) {
          if (cursors.has(nextCursor)) return _failure("invalidResponse");
          cursors.add(nextCursor);
        }
      } while (nextCursor);
      return _success([...connections.values()]);
    },
    disconnect: async (connectionId: string): Promise<ActionResult<void>> => {
      const result = await api.agent.disconnect(connectionId);
      return "error" in result ? _failure(result.error) : _success(undefined);
    },
  };

  return {
    session,
    user,
    transcript,
    aiContent,
    subscription,
    tag: tags,
    dictation,
    search,
    app,
    agent,
  };
};

export type Actions = ReturnType<typeof createActions>;

// Local mirror of the server's delete: drop it from the list and from every
// tag, removing tags it leaves empty.
const _removeTranscript = (store: ActionStore, transcriptId: string) => {
  store.setState((state) => {
    const currentTags = state.userData?.allTags;
    const allTags = currentTags
      ?.map((tag) => {
        const hadReference = tag.notesIds?.includes(transcriptId) ?? false;
        return {
          tag,
          hadReference,
          notesIds: (tag.notesIds || []).filter(
            (noteId) => noteId !== transcriptId,
          ),
        };
      })
      .filter(
        ({ hadReference, notesIds }) =>
          !hadReference || notesIds.length > 0,
      )
      .map(({ tag, notesIds }) => ({ ...tag, notesIds }));

    return {
      transcripts: state.transcripts.filter(
        (item) => item.id !== transcriptId,
      ),
      userData:
        state.userData && allTags
          ? { ...state.userData, allTags }
          : state.userData,
    };
  });
};

const _replaceTranscript = (
  store: ActionStore,
  transcriptId: string,
  replacement: Transcript,
) => {
  store.setState((state) => ({
    transcripts: state.transcripts.map((item) =>
      item.id === transcriptId ? replacement : item,
    ),
  }));
};

const _reconcileTranscript = (
  store: ActionStore,
  received: Transcript,
  options?: { skipUnchangedRevision?: boolean },
) => {
  const current = store
    .getState()
    .transcripts.find((item) => item.id === received.id);
  if (
    options?.skipUnchangedRevision &&
    current?.updatedAt &&
    received.updatedAt &&
    current.updatedAt === received.updatedAt &&
    current.status === received.status &&
    current.statusDetail === received.statusDetail
  ) {
    return;
  }

  store.setState((state) => {
    const exists = state.transcripts.some((item) => item.id === received.id);
    return {
      transcripts: exists
        ? state.transcripts.map((item) =>
            item.id === received.id ? received : item,
          )
        : [...state.transcripts, received].sort(
            (left, right) =>
              new Date(right.createdAt).getTime() -
              new Date(left.createdAt).getTime(),
          ),
    };
  });
};

const _startTranscriptOperation = async (
  store: ActionStore,
  telemetry: ActionTelemetry,
  transcriptId: string,
  operation: "speakerLabels",
  request: () => Promise<
    | { ok: true; value: { transcript?: Transcript } | undefined }
    | { ok: false; error: ClientError }
  >,
): Promise<ActionResult<Transcript | undefined>> => {
  const current = store
    .getState()
    .transcripts.find((item) => item.id === transcriptId);
  if (!current) return _failure("TranscriptNotFound");
  const result = await request();
  if ("error" in result) return _failure(result.error);
  if (result.value?.transcript) {
    _replaceTranscript(store, transcriptId, result.value.transcript);
  }
  telemetry.track("transcript.processingStarted", {
    transcriptId,
    operation,
  });
  return _success(result.value?.transcript);
};

const _requestAiContent = async (
  store: ActionStore,
  telemetry: ActionTelemetry,
  transcriptId: string,
  contentType: "translation" | "summary" | "flashcard" | "quiz",
  request: () => Promise<
    | { ok: true; value: { transcript?: Transcript } | undefined }
    | { ok: false; error: ClientError }
  >,
): Promise<ActionResult<void>> => {
  const result = await request();
  if ("error" in result) return _failure(result.error);
  // A regeneration response carries the transcript with the old content
  // cleared. It must land in the store before polling starts — pollStatus
  // short-circuits on the cached transcript, and stale content would make
  // it resolve instantly without a single fetch.
  const transcript = result.value?.transcript;
  if (transcript) {
    store.setState((state) => ({
      transcripts: state.transcripts.map((item) =>
        item.id === transcript.id ? transcript : item,
      ),
    }));
  }
  telemetry.track("aiContent.requested", {
    transcriptId,
    feature: contentType,
  });
  return _success(undefined);
};

const _setWorkHealth = (
  store: ActionStore,
  key: string,
  health: WorkHealth,
) => {
  store.setState((state) => ({
    workHealth: { ...state.workHealth, [key]: health },
  }));
};

const _pollIntervalMs = (elapsedMs: number): number => {
  if (elapsedMs < 2 * 60 * 1_000) return 2_000;
  if (elapsedMs < 10 * 60 * 1_000) return 10_000;
  return 30_000;
};

const _tagColors = [
  "#EF4444",
  "#F97316",
  "#EAB308",
  "#22C55E",
  "#14B8A6",
  "#3B82F6",
  "#8B5CF6",
  "#EC4899",
  "#6366F1",
] as const;

const _tagColor = (name: string): string => {
  let hash = 0;
  for (let index = 0; index < name.length; index += 1) {
    hash = name.charCodeAt(index) + ((hash << 5) - hash);
  }
  return _tagColors[Math.abs(hash) % _tagColors.length];
};
